import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import adminService from "../../api/services/adminService";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import logo1 from "../../assests/logo1.png";
import { FiEye, FiEyeOff, FiLock, FiMail } from "react-icons/fi";
import { persistAdminSession } from "../../utils/adminSession";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e?.preventDefault?.();

    const emailTrimmed = email.trim();
    const passwordTrimmed = password.trim();
    const isEmailValid = /.+@.+\..+/.test(emailTrimmed);

    if (!emailTrimmed || !passwordTrimmed) {
      toast.error("Please fill in all required fields!");
      return;
    }
    if (!isEmailValid) {
      toast.error("Please enter a valid email address!");
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);
    const toastId = toast.loading("Signing in...");
    try {
      const res = await adminService.login({
        email: emailTrimmed,
        password: passwordTrimmed,
      });

      Cookies.remove("token");
      Cookies.remove("naseebiadmin");
      localStorage.clear();

      if (res?.token) {
        persistAdminSession(res.token, res.user);
      } else if (res?.user) {
        persistAdminSession(null, res.user);
      }

      toast.success("Logged in successfully!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const message = err?.message || err?.error || err?.msg || "Login failed";
      toast.error(typeof message === "string" ? message : "Unknown error occurred!");
    } finally {
      toast.dismiss(toastId);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6FA] lg:flex">
      {/* Brand panel */}
      <div className="relative hidden lg:flex lg:w-[46%] xl:w-[42%] flex-col justify-between overflow-hidden bg-gradient-to-br from-[#1a1a2e] via-[#2d1f3d] to-[#4a1942] p-12 text-white">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-[#FB2F38] blur-3xl" />
          <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-[#ff6b6b] blur-3xl" />
        </div>

        <div className="relative z-10">
          <img src={logo1} alt="Naseebi" className="h-12 w-auto brightness-0 invert" />
        </div>

        <div className="relative z-10 max-w-md">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-red-200">
            Admin Panel
          </p>
          <h1 className="text-4xl font-bold leading-tight xl:text-5xl">
            Manage Naseebi with confidence
          </h1>
          <p className="mt-4 text-base leading-relaxed text-white/75">
            Sign in to access users, subscriptions, support, sales, and reporting tools.
          </p>
        </div>

        <p className="relative z-10 text-sm text-white/50">
          Secure access for authorized team members only.
        </p>
      </div>

      {/* Login form */}
      <div className="flex flex-1 items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 flex flex-col items-center lg:hidden">
            <img src={logo1} alt="Naseebi" className="h-12 w-auto" />
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-[0_20px_50px_rgba(15,23,42,0.08)] sm:p-10">
            <div className="mb-8 text-center lg:text-left">
              <h2 className="text-3xl font-bold text-[#0F172A]">Welcome back</h2>
              <p className="mt-2 text-sm text-gray-500">
                Enter your credentials to continue to the admin dashboard.
              </p>
            </div>

            <form className="space-y-5" onSubmit={handleLogin} noValidate>
              <div>
                <label htmlFor="login-email" className="mb-2 block text-sm font-semibold text-gray-700">
                  Email
                </label>
                <div className="relative">
                  <FiMail className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    id="login-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="h-12 w-full rounded-xl border border-gray-200 bg-gray-50/60 pl-11 pr-4 text-sm text-gray-900 outline-none transition focus:border-[#FB565F] focus:bg-white focus:ring-2 focus:ring-[#FB565F]/20"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="login-password" className="mb-2 block text-sm font-semibold text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <FiLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    id="login-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="h-12 w-full rounded-xl border border-gray-200 bg-gray-50/60 pl-11 pr-12 text-sm text-gray-900 outline-none transition focus:border-[#FB565F] focus:bg-white focus:ring-2 focus:ring-[#FB565F]/20"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="h-12 w-full rounded-xl bg-[#FB2F38] text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-red-500/20 transition hover:bg-[#e42831] disabled:cursor-not-allowed disabled:opacity-70">
                {isSubmitting ? "Signing in..." : "Sign in"}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm font-semibold text-[#D12A30] transition hover:underline">
                  Forgot password?
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
